<template>
  <div class="step-form-container">
    <validation-observer v-slot="{ handleSubmit }" ref="refFormObserver">
      <b-form
        class="p-2"
        @submit.prevent="handleSubmit(onFinish)"
        @reset.prevent="resetForm"
      >
        <!-- Header with back button and step indicator -->
        <div
          class="step-form-header d-flex align-items-center justify-content-between p-2"
        >
          <div class="d-flex align-items-center">
            <b-button variant="link" class="p-0 mr-1" @click="handleBack">
              <feather-icon icon="ArrowLeftIcon" size="20" />
            </b-button>
            <h4 class="mb-0">{{ stepTitle }}</h4>
          </div>
          <div class="step-indicators d-flex align-items-center">
            <div
              v-for="(step, i) in visibleSteps"
              :key="i"
              class="d-flex align-items-center"
            >
              <div
                class="step-dot"
                :class="{
                  'step-active': i + startStep === currentStep,
                  'step-completed': i + startStep < currentStep,
                  'step-pending': i + startStep > currentStep,
                }"
              >
                <feather-icon
                  v-if="i + startStep < currentStep"
                  icon="CheckIcon"
                  size="12"
                />
                <span v-else>{{ i + 1 }}</span>
              </div>
              <div v-if="i < visibleSteps.length - 1" class="step-line" />
            </div>
          </div>
        </div>

        <hr class="mt-0" />

        <!-- Step content with transitions -->
        <div class="step-form-body" :class="{ 'flex-unset': !showFilters }">
          <!-- Step 0: Crear bitácora -->
          <Transition name="slide-left-right" mode="out-in">
            <div v-if="currentStep === 0" key="step-0" class="step-content p-2">
              <BaseInput
                v-model="formData.folio"
                id="folio"
                rules="required"
                :label="$t('operational_security.operational_processes.folio')"
                :validation_field_name="
                  $t('operational_security.operational_processes.folio')
                "
                :getValidationState="getValidationState"
              />
            </div>

            <!-- Step 1: Información del proceso -->
            <div v-else-if="currentStep === 1" key="step-1" class="step-content p-2">
              <div class="form-grid">
                <!-- Inmueble (full width) -->
                <div class="grid-full">
                  <validation-provider
                    v-slot="validationContext"
                    :name="$t('operational_security.operational_processes.unit')"
                    rules="required"
                  >
                    <b-form-group
                      :label="$t('operational_security.operational_processes.unit')"
                      label-for="unit"
                    >
                      <base-select-pagination
                        url="/v1/unit"
                        input-id="id_unit"
                        :option-mapper="unitMapperOption"
                        :value="formData.unit"
                        @input="onChangeUnit"
                      >
                        <template #option="{ description, sirh, rems, id_seproban }">
                          <span class="d-block text-uppercasee">{{ description }}</span>
                          <span class="text-muted" style="font-size: 13px">
                            SIRH: {{ sirh }} REMS: {{ rems }} ID SEPROBAN:
                            {{ id_seproban }}
                          </span>
                        </template>
                      </base-select-pagination>
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>
                </div>

                <!-- SIRH -->
                <base-input
                  id="sirh"
                  :label="$t('operational_security.operational_processes.sirh')"
                  :value="unitData.sirh"
                  disabled
                />

                <!-- REMS -->
                <base-input
                  id="rems"
                  :label="$t('operational_security.operational_processes.rems')"
                  :value="unitData.rems"
                  disabled
                />

                <!-- ID SEPROBAN -->
                <base-input
                  id="id_seproban"
                  :label="$t('operational_security.operational_processes.id_seproban')"
                  :value="unitData.id_seproban"
                  disabled
                />

                <!-- Categorías -->
                <base-input
                  id="categorias"
                  :label="$t('operational_security.operational_processes.category')"
                  :value="unitData.category"
                  disabled
                />

                <!-- Estado -->
                <base-input
                  id="state"
                  :label="$t('operational_security.operational_processes.state')"
                  :value="unitData.state"
                  disabled
                />

                <!-- Municipio -->
                <base-input
                  id="municypality"
                  :label="$t('operational_security.operational_processes.municipality')"
                  :value="unitData.municipality"
                  disabled
                />

                <!-- Subregión -->
                <base-input
                  id="subregion"
                  :label="$t('operational_security.operational_processes.subregion')"
                  :value="unitData.subregion"
                  disabled
                />

                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Fecha/hora ingreso -->
                <validation-provider
                  #default="validationContext"
                  :name="
                    $t('operational_security.operational_processes.check_in_datetime')
                  "
                  rules="required"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.check_in_datetime')
                    "
                    label-for="check_in_datetime"
                  >
                    <b-input-group>
                      <b-form-input
                        id="check_in_datetime"
                        v-model="formData.check_in_datetime"
                        :placeholder="
                          $t(
                            'operational_security.operational_processes.generate_timestamp'
                          )
                        "
                        readonly
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          @click="generateTimestamp('check_in_datetime')"
                          class="padding-0-1"
                        >
                          <feather-icon icon="ClockIcon" size="16" />
                        </b-button>
                      </b-input-group-append>
                      <b-form-invalid-feedback
                        :state="getValidationState(validationContext)"
                      >
                        {{ validationContext.errors[0] }}
                      </b-form-invalid-feedback>
                    </b-input-group>
                  </b-form-group>
                </validation-provider>

                <!-- Proveedor -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.company')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.company')"
                    label-for="company"
                  >
                    <v-select
                      id="proveedor"
                      v-model="formData.id_company"
                      :options="companiesOptions"
                      :placeholder="
                        $t(
                          'operational_security.operational_processes.company_placeholder'
                        )
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :reduce="(val) => val.value"
                      @input="onChangeProvider"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Técnico asignado -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.employee')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.employee')"
                    label-for="employee"
                    :class="{ 'disabled-select': isDisabledForm }"
                  >
                    <v-select
                      id="tecnico_asignado"
                      :value="formData.employees"
                      @input="handleEmployeesChange"
                      :options="employeesOptions"
                      :placeholder="
                        $t(
                          'operational_security.operational_processes.employee_placeholder'
                        )
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :calculate-position="withPopper"
                      :disabled="!formData.id_company || isDisabledForm"
                      multiple
                      label="full_name_id"
                      :class="{ 'disabled-select': isDisabledForm }"
                      :reduce="
                        (val) => ({
                          id: val.id,
                          full_name_id: val.full_name_id,
                          image: val.image,
                          employee_id: val.employee_id,
                          first_name: val.first_name,
                          last_name: val.last_name,
                        })
                      "
                    >
                      <template
                        #option="{
                          image,
                          full_name_id,
                          employee_id,
                          first_name,
                          last_name,
                        }"
                      >
                        <div class="d-flex align-items-center">
                          <div>
                            <img
                              v-if="image"
                              :src="image.url"
                              alt="Sin imagen"
                              class="avatar"
                              style="max-width: 35px; max-height: 35px"
                            />
                            <div
                              v-else
                              class="avatar d-flex align-items-center justify-content-center"
                              style="max-width: 35px; max-height: 35px"
                            >
                              <feather-icon
                                icon="ImageIcon"
                                size="15"
                                class="align-middle text-body"
                              />
                            </div>
                          </div>
                          <div class="pl-1">
                            <span class="text-truncate d-block">{{ employee_id }}</span>
                            <span class="text-truncate text-uppercase"
                              >{{ first_name }} {{ last_name }}</span
                            >
                          </div>
                        </div>
                      </template>
                    </v-select>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Número de reporte de atención -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.report_number')"
                  rules="required"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.report_number')
                    "
                    label-for="report_number"
                  >
                    <b-form-input
                      id="report_number"
                      v-model="formData.report_number"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Sirena deshabilitada en Remote a la entrada -->
                <!-- <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.siren_disabled')"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.siren_disabled')
                    "
                    label-for="siren_disabled"
                  >
                    <b-form-input
                      id="siren_disabled"
                      v-model="formData.siren_disabled"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider> -->

                <!-- Sirena habilitada y prueba de sirena a la salida -->
                <!-- <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.siren_enabled')"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.siren_enabled')
                    "
                    label-for="siren_enabled"
                  >
                    <b-form-input
                      id="siren_enabled"
                      v-model="formData.siren_enabled"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider> -->

                <!-- Altura infrarrojos patio público -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.infrared_height')"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.infrared_height')
                    "
                    label-for="siren_enabled"
                  >
                    <b-form-input
                      id="siren_enabled"
                      v-model="formData.infrared_height"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Infrarrojo validado -->
                <validation-provider
                  #default="validationContext"
                  :name="
                    $t('operational_security.operational_processes.infrared_validated')
                  "
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.infrared_validated')
                    "
                    label-for="siren_enabled"
                  >
                    <b-form-input
                      id="siren_enabled"
                      v-model="formData.infrared_validated"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Separator -->
                <div
                  class="grid-full"
                  v-if="formData.employees && formData.employees.length"
                >
                  <!-- Tabla de empleados -->
                  <employees-table :employees="formData.employees" />
                </div>

                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Actividades o tipo de proyecto (full width) -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.activities')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.activities')"
                    class="grid-full"
                  >
                    <div class="actividades-checkboxes">
                      <b-form-checkbox
                        v-for="(actividad, idx) in activityOptions"
                        :key="idx"
                        v-model="formData.activities"
                        :value="actividad.value"
                        class="mb-50"
                        :disabled="isDisabledForm"
                      >
                        {{ actividad.label }}
                      </b-form-checkbox>
                    </div>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Sistemas (full width) -->
                <validation-provider
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.system')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.system')"
                    class="grid-full"
                  >
                    <div class="sistemas-checkboxes">
                      <b-form-checkbox
                        v-for="(sistema, idx) in sistemasOptions"
                        :key="'sis-' + idx"
                        v-model="formData.systems"
                        :value="sistema"
                        class="mb-50"
                        :disabled="isDisabledForm"
                      >
                        {{ sistema }}
                      </b-form-checkbox>
                    </div>
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Activar modo de pruebas -->
                <validation-provider
                  #default="validationContext"
                  :name="
                    $t('operational_security.operational_processes.is_test_mode_enabled')
                  "
                  rules="required"
                >
                  <b-form-group
                    :label="
                      $t(
                        'operational_security.operational_processes.is_test_mode_enabled'
                      )
                    "
                    label-for="is_test_mode_enabled"
                  >
                    <v-select
                      id="is_test_mode_enabled"
                      v-model="formData.is_test_mode_enabled"
                      :options="[
                        {
                          label: 'Sí',
                          value: true,
                        },
                        {
                          label: 'No',
                          value: false,
                        },
                      ]"
                      :placeholder="
                        $t(
                          'operational_security.operational_processes.is_test_mode_enabled_placeholder'
                        )
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :reduce="(val) => val.value"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </div>

              <!-- Campos restantes -->
              <div
                class="form-grid"
                v-if="isTestModeEnabled !== null && isTestModeEnabled"
              >
                <!-- Separator -->
                <hr class="grid-full" />

                <!-- Tipo de mantenimiento -->
                <validation-provider
                  #default="validationContext"
                  :name="
                    $t('operational_security.operational_processes.type_of_maintenance')
                  "
                  rules="required"
                >
                  <b-form-group
                    :label="
                      $t('operational_security.operational_processes.type_of_maintenance')
                    "
                    label-for="type_of_maintenance"
                  >
                    <v-select
                      id="type_of_maintenance"
                      :value="formData.type_of_maintenance"
                      @input="onChangeTypeOfMaintenance"
                      :options="tipoMantenimientoOptions"
                      :placeholder="
                        $t(
                          'operational_security.operational_processes.type_of_maintenance_placeholder'
                        )
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Panel -->
                <validation-provider
                  v-if="formData.type_of_maintenance === 'Parcial'"
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.panel')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.panel')"
                    label-for="panel"
                    :description="
                      isLoadingPanels
                        ? null
                        : panelOptions.length
                        ? null
                        : $t('operational_security.operational_processes.panels_empty')
                    "
                  >
                    <v-select
                      id="id_panel"
                      :value="formData.panel"
                      @input="onChangePanel"
                      :options="panelOptions"
                      :placeholder="
                        $t('operational_security.operational_processes.panel_placeholder')
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :disabled="isDisabledForm"
                      :loading="isLoadingPanels"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>

                <!-- Maintenance tenant for total and partial maintenance -->
                <validation-provider
                  v-if="['Total', 'Parcial'].includes(formData.type_of_maintenance)"
                  #default="validationContext"
                  :name="$t('operational_security.operational_processes.tenant')"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('operational_security.operational_processes.tenant')"
                    label-for="id_tenant"
                  >
                    <v-select
                      id="id_tenant"
                      v-model="formData.id_tenant"
                      :options="tenantOptions"
                      :reduce="(val) => val.value"
                      :placeholder="
                        $t(
                          'operational_security.operational_processes.tenant_placeholder'
                        )
                      "
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :calculate-position="withPopper"
                      :disabled="isDisabledForm"
                    />
                    <b-form-invalid-feedback
                      :state="getValidationState(validationContext)"
                    >
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
                <!-- Separator -->
                <hr class="grid-full" />
              </div>

              <div v-if="isTestModeEnabled !== null && isTestModeEnabled">
                <!-- Salidas parciales -->
                <b-button
                  variant="secondary"
                  @click="addPartialCheckout"
                  class="mb-2"
                  :disabled="isDisabledForm"
                >
                  {{
                    $t("operational_security.operational_processes.add_partial_checkout")
                  }}
                </b-button>
                <!-- Descripción parcial -->
                <div
                  v-for="(partial, idx) in formData.partial_checkouts"
                  :key="idx"
                  class="form-grid"
                >
                  <!-- Fecha/hora inicio -->
                  <validation-provider
                    v-slot="validationContext"
                    :name="`${$t(
                      'operational_security.operational_processes.partial_checkout_check_in_datetime'
                    )} ${idx + 1}`"
                    rules="required"
                  >
                    <b-form-group
                      :label="
                        $t(
                          'operational_security.operational_processes.partial_checkout_check_in_datetime'
                        )
                      "
                      :label-for="`partial_checkout_check_in_datetime_${idx}`"
                    >
                      <b-input-group>
                        <b-form-input
                          :id="`partial_checkout_check_in_datetime_${idx}`"
                          v-model="partial.start_datetime"
                          :placeholder="
                            $t(
                              'operational_security.operational_processes.generate_timestamp'
                            )
                          "
                          readonly
                          :state="getValidationState(validationContext)"
                          :disabled="isDisabledForm"
                        />
                        <b-input-group-append>
                          <b-button
                            variant="outline-primary"
                            @click="partial.start_datetime = generateTimestamp()"
                            class="padding-0-1"
                          >
                            <feather-icon icon="ClockIcon" size="16" />
                          </b-button>
                        </b-input-group-append>
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-input-group>
                    </b-form-group>
                  </validation-provider>

                  <!-- Fecha/hora salida -->
                  <b-form-group
                    :label="
                      $t(
                        'operational_security.operational_processes.partial_checkout_check_out_datetime'
                      )
                    "
                    :label-for="`partial_checkout_check_out_datetime_${idx}`"
                  >
                    <b-input-group>
                      <b-form-input
                        :id="`partial_checkout_check_out_datetime_${idx}`"
                        v-model="partial.end_datetime"
                        :placeholder="
                          $t(
                            'operational_security.operational_processes.generate_timestamp'
                          )
                        "
                        readonly
                        :disabled="isDisabledForm"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          @click="partial.end_datetime = generateTimestamp()"
                          class="padding-0-1"
                        >
                          <feather-icon icon="ClockIcon" size="16" />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-form-group>

                  <!-- Descripción parcial -->
                  <b-form-group
                    :label="
                      $t(
                        'operational_security.operational_processes.partial_checkout_description'
                      )
                    "
                    :label-for="`partial_checkout_description_${idx}`"
                  >
                    <b-form-input
                      :id="`partial_checkout_description_${idx}`"
                      v-model="partial.description"
                      :disabled="isDisabledForm"
                    />
                  </b-form-group>

                  <!-- Reactivar sistemas -->
                  <b-form-group
                    :label="
                      $t(
                        'operational_security.operational_processes.partial_checkout_is_systems_enabled'
                      )
                    "
                    :label-for="`partial_checkout_is_systems_enabled_${idx}`"
                  >
                    <b-form-checkbox
                      class="my-1"
                      switch
                      v-model="partial.is_systems_enabled"
                      :disabled="isDisabledForm"
                    >
                    </b-form-checkbox>
                  </b-form-group>

                  <div>
                    <b-button
                      variant="danger"
                      @click="removePartialCheckout(idx)"
                      class="mr-2"
                      :disabled="isDisabledForm"
                    >
                      {{ $t("operational_security.operational_processes.delete") }}
                    </b-button>
                  </div>
                </div>
              </div>

              <DevicesTable
                v-if="formData.device_proxies.length"
                :device_proxies="formData.device_proxies"
              />
            </div>

            <!-- Step 2: Configuración de panel (solo si Parcial) -->
            <div v-else-if="currentStep === 2" key="step-2" class="step-content p-2">
              <operational-devices
                v-if="formData.panel.id"
                :panel-id="formData.panel.id"
                :panel-uuid="formData.panel.uuid"
                @maintenance-toggle="onMaintenanceToggle"
                :show-filters="showFilters"
                :maintenance-devices="maintenanceDevices"
                :is-disabled-form="isDisabledForm"
                :form-data="formData"
                @form-data-changed="onFormDataChanged"
                @update:reminder-minutes="onUpdateReminderMinutes"
              />
            </div>
          </Transition>
        </div>

        <!-- Footer with navigation buttons -->
        <hr />
        <div class="step-form-footer d-flex justify-content-end p-2 pt-0">
          <b-button variant="outline-secondary" class="mr-1" @click="handleBack">
            {{ currentStep === startStep ? $t("actions.cancel") : $t("actions.go_back") }}
          </b-button>
          <b-button v-if="currentStep < lastStep" variant="primary" type="submit">
            {{ $t("actions.next") }}
          </b-button>
          <b-button
            v-else
            variant="success"
            type="submit"
            :disabled="isSaving || isDisabledForm"
          >
            <b-spinner v-if="isSaving" small />
            {{ $t("actions.save") }}
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref, computed, onMounted, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";
import popperHelper from "@/libs/popper-helper";
import store from "@/store";
import OperationalDevices from "./Devices.vue";
import { format } from "date-fns";

import useStepForm from "./useStepForm";
import { getResponseErrors } from "@/utils/responseErrors";
import formValidation from "@core/comp-functions/forms/form-validation";

import { BaseInput, BaseSelect } from "@/components/Form";
import EmployeesTable from "./EmployeesTable.vue";
import DevicesTable from "./DevicesTable.vue";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BaseSelectPagination from "@/components/Form/BaseSelectPagination.vue";
import axios from "@/libs/axios";

export default {
  name: "OperationalProcessStepForm",
  components: {
    vSelect,
    OperationalDevices,
    ValidationProvider,
    ValidationObserver,
    BaseInput,
    BaseSelect,
    EmployeesTable,
    DevicesTable,
    BaseSelectPagination,
  },
  props: {
    isAdd: {
      type: Boolean,
      default: true,
    },
    itemEdit: {
      type: Object,
      default: () => ({}),
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    firstItem: {
      type: Object,
      default: null,
    },
    unitOptions: {
      type: Array,
      default: () => [],
    },
    inititalFormStep: {
      type: Number,
      default: 1,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const hasFirstItem = !!props.firstItem;
    //const startStep = !props.isAdd ? 1 : 0;
    const startStep = 1;
    const currentStep = ref(props.inititalFormStep);

    const isLoadingPanels = ref(false);
    const isLoadingPanelUsers = ref(false);

    const isSaving = ref(false);
    const panelOptions = ref([]);
    const maintenanceDevices = ref(props.isAdd ? [] : props.itemEdit.device_proxies);

    const blankData = {
      folio: "",
      check_in_datetime: "",
      is_test_mode_enabled: null,
      check_out_datetime: "",
      report_number: "",
      type_of_maintenance: null,
      activities: [],
      systems: [],
      unit: null,
      id_unit: null,
      id_company: null,
      employees: null,
      id_tenant: null,
      panel: {
        id: null,
        uuid: null,
      },
      partial_checkouts: [],
      device_proxies: [],
      siren_enabled: null,
      siren_disabled: null,
      infrared_height: null,
      infrared_validated: null,
      panel_user: null,
      panel_user_code: null,
    };

    onMounted(() => {
      store.dispatch("companies/getAll");
      store.dispatch("tenant/getAll");
    });

    const tenantOptions = computed(() => store.getters["tenant/getSelectedItems"]);
    const companiesOptions = computed(() => store.getters["companies/getSelectedItems"]);
    const employeesOptions = computed(() => store.getters["employees/getAll"]);

    const { refFormObserver, getValidationState, resetForm } = formValidation(blankData);

    const formData = ref(
      props.isAdd
        ? {
            ...JSON.parse(JSON.stringify(blankData)),
            // Inherit folio from first item if it exists
            ...(props.firstItem
              ? { folio_bitacora: props.firstItem.folio_bitacora }
              : {}),
          }
        : { ...JSON.parse(JSON.stringify(blankData)), ...props.itemEdit }
    );

    const isDisabledForm = computed(() => formData.value.status === "finished");
    const isTestModeEnabled = computed(() => formData.value.is_test_mode_enabled);

    const addPartialCheckout = () => {
      formData.value.partial_checkouts.push({
        start_datetime: "",
        end_datetime: "",
        description: null,
        is_systems_enabled: false,
      });
    };

    const removePartialCheckout = (index) => {
      formData.value.partial_checkouts.splice(index, 1);
    };

    const unitData = computed(() => {
      return {
        sirh: formData.value.unit?.sirh ?? null,
        rems: formData.value.unit?.rems ?? null,
        id_seproban: formData.value.unit?.id_seproban ?? null,
        category: formData.value.unit?.category?.name ?? null,
        state: formData.value.unit?.region?.name ?? null,
        municipality: formData.value.unit?.municipality?.name ?? null,
        subregion: formData.value.unit?.sector?.name ?? null,
      };
    });

    const getPanels = async (unitId) => {
      isLoadingPanels.value = true;
      try {
        const response = await store.dispatch("dmp_panels/getAll", {
          units: [unitId],
        });

        const panels = response.data.map((panel) => ({
          ...panel,
          value: panel.id,
          label: panel.name,
          uuid: panel.uuid,
        }));

        panelOptions.value = panels;
        formData.value.panel = null;

        if (panels.length) {
          formData.value.panel = panels[0];
        }
      } catch (error) {
      } finally {
        isLoadingPanels.value = false;
      }
    };

    watch(
      () => formData.value.unit,
      async (unit) => {
        formData.value.id_unit = unit?.id ?? null;

        if (!unit) return;

        getPanels(unit.id);
      },
      { immediate: true }
    );

    watch(
      () => formData.value.id_company,
      (id_company) => {
        if (!id_company) return;

        store.dispatch("employees/getAll", {
          id_company,
        });
      },
      { immediate: true }
    );

    const onChangeProvider = (id_company) => {
      formData.value.employees = [];
    };

    // Options
    const totalMaintenance = "Total";
    const partialMaintenance = "Parcial";
    const tipoMantenimientoOptions = [totalMaintenance, partialMaintenance];

    const sistemasOptions = [
      "Sistema de alarmas",
      "Sistema de CCTV",
      "Sistema de control de accesos",
    ];

    const { t } = useI18nUtils();

    // Step names
    const allStepNamesComputed = computed(() => [
      t("operational_security.operational_processes.step_create"),
      t("operational_security.operational_processes.step_information"),
      t("operational_security.operational_processes.step_panel_configuration"),
    ]);

    const visibleSteps = computed(() => {
      const allStepNames = allStepNamesComputed.value;
      const names = !props.isAdd ? allStepNames.slice(1) : allStepNames;

      if (formData.value.type_of_maintenance === "Parcial") {
        return names;
      }
      // Without "Crear bitácora": only "Información del proceso"
      // With "Crear bitácora": "Crear bitácora" + "Información del proceso"
      return !props.isAdd ? names.slice(0, 1) : names.slice(0, 2);
    });

    const lastStep = computed(() => {
      if (isTestModeEnabled.value !== null && !isTestModeEnabled.value) {
        return 1;
      }

      return formData.value.type_of_maintenance === "Parcial" ? 2 : 1;
    });

    const stepTitle = computed(() => {
      const allStepNames = allStepNamesComputed.value;
      return allStepNames[currentStep.value] || "";
    });

    const hasInstalacion = computed(() => {
      return !!(formData.value.inmueble || formData.value.inmueble_texto);
    });

    // Navigation
    const handleBack = () => {
      if (currentStep.value > startStep) {
        currentStep.value -= 1;
      } else {
        emit("close");
      }
    };

    const nextStep = () => {
      if (currentStep.value < lastStep.value) {
        currentStep.value += 1;
      }
    };

    const onFinish = () => {
      if (currentStep.value === lastStep.value) {
        let dispatch;

        isSaving.value = true;

        if (props.isAdd) {
          dispatch = store.dispatch("external_personnel_access_control/add", {
            ...formData.value,
            maintenance_devices: maintenanceDevices.value,
            employees: formData.value.employees.map((e) => e.id),
          });
        } else {
          const req = {
            id: formData.value.id,
            params: {
              ...formData.value,
              maintenance_devices: maintenanceDevices.value,
              employees: formData.value.employees.map((e) => e.id),
            },
          };

          dispatch = store.dispatch("external_personnel_access_control/edit", req);
        }

        dispatch
          .then((response) => {
            if (response.success) {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "CheckIcon",
                  variant: "success",
                },
              });

              emit("saved");

              formData.value = blankData;
            } else {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: "AlertTriangleIcon",
                  variant: "danger",
                },
              });
            }
          })
          .catch((response) => {
            const { message, strValidationErrors } = getResponseErrors(response);

            toast({
              component: ToastificationContent,
              props: {
                title: strValidationErrors ? strValidationErrors : message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            isSaving.value = false;
          });
      }

      nextStep();
    };

    const generateTimestamp = (field) => {
      const now = new Date();
      const date = format(new Date(), "yyyy-MM-dd HH:mm:ss");
      if (field) {
        formData.value[field] = date;
      }

      return date;
    };

    const ejecutarComandoTest = () => {
      console.log("Ejecutar Comando Test");
      toast({
        component: ToastificationContent,
        props: {
          title: "Comando test ejecutado",
          icon: "CheckIcon",
          variant: "success",
        },
      });
    };

    const { activityOptions } = useStepForm();

    const handleEmployeesChange = (employees) => {
      if (employees.length > 5) {
        toast({
          component: ToastificationContent,
          props: {
            title: "El número máximo de empleados es de 5",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }
      formData.value.employees = employees;
    };

    const unitMapperOption = (item) => ({
      ...item,
      id: item.id,
      label: item.description,
    });

    const onChangeUnit = (unit) => {
      formData.value.unit = unit;
      formData.value.panel = null;
      formData.value.panel_user = null;
      formData.value.panel_user_code = null;

      maintenanceDevices.value = [];
    };

    // Suggest the testing tenant for total and partial maintenance.
    const onChangeTypeOfMaintenance = (value) => {
      formData.value.id_tenant = null;
      formData.value.type_of_maintenance = value;

      if ([totalMaintenance, partialMaintenance].includes(value)) {
        const defaultSelectedTenant = "pruebas y entregas";

        const maintenanceTenant = tenantOptions.value.find(
          (item) => item.label?.toLowerCase()?.trim() === defaultSelectedTenant
        );

        if (!maintenanceTenant) return;

        formData.value.id_tenant = maintenanceTenant.value;
      }
    };

    const onChangePanel = (value) => {
      formData.value.panel = value;
    };

    const onFormDataChanged = (payload) => {
      formData.value[payload.key] = payload.value;
    };

    const onUpdateReminderMinutes = (payload) => {
      const { device_proxy_id, reminder_minutes } = payload;

      maintenanceDevices.value = maintenanceDevices.value.map((device) => {
        if (device.id_device_proxy === device_proxy_id) {
          device.new_reminder_minutes = reminder_minutes;
          device.reminder_sent_at = null;
        }
        return device;
      });
    }

    return {
      isDisabledForm,

      activityOptions,
      companiesOptions,
      employeesOptions,
      unitData,
      onChangeProvider,
      panelOptions,

      refFormObserver,
      getValidationState,
      resetForm,
      addPartialCheckout,
      removePartialCheckout,

      currentStep,
      startStep,
      formData,
      stepTitle,
      visibleSteps,
      lastStep,
      hasInstalacion,

      // Options
      tenantOptions,
      sistemasOptions,
      tipoMantenimientoOptions,

      // Actions
      handleBack,
      nextStep,
      onFinish,
      generateTimestamp,
      ejecutarComandoTest,

      withPopper: popperHelper.withPopper,

      maintenanceDevices,
      isSaving,
      handleEmployeesChange,
      isTestModeEnabled,

      onMaintenanceToggle: ({ type, device, value }) => {
        if (value) {
          // Add device if not already tracked
          const exists = maintenanceDevices.value.some(
            (d) => d.id_device_proxy == device.device_proxy_id
          );

          if (!exists) {
            maintenanceDevices.value.push({
              id: device.id,
              type,
              text: device.text,
              id_device_proxy: device.device_proxy_id,
            });
          }
        } else {
          // Remove device
          maintenanceDevices.value = maintenanceDevices.value.filter(
            (d) => !(d.id_device_proxy == device.device_proxy_id)
          );
        }
      },

      unitMapperOption,
      onChangeUnit,
      onChangeTypeOfMaintenance,
      onChangePanel,
      isLoadingPanels,
      isLoadingPanelUsers,
      onFormDataChanged,
      //
      onUpdateReminderMinutes
    };
  },
};
</script>

<style lang="scss">
.disabled-select .vs__selected {
  color: #6e6b7b !important;
}
</style>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/transitions";

.step-form-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.step-form-body {
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #aaa;
    border-radius: 4px;
  }
}

.step-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-active {
  background-color: var(--primary);
  color: #fff;
}

.step-completed {
  background-color: var(--success);
  color: #fff;
}

.step-pending {
  background-color: #e9ecef;
  color: #6c757d;
}

.step-line {
  width: 24px;
  height: 2px;
  background-color: #e9ecef;
  margin: 0 4px;
}

.actividades-checkboxes,
.sistemas-checkboxes {
  overflow-y: auto;
  padding-right: 0.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0 1.5rem;
  align-items: start;
}

.grid-full {
  grid-column: 1 / -1;
}

.flex-unset {
  flex: unset;
}

.padding-0-1 {
  padding: 0 1rem;
}

.dark-layout {
  .step-pending {
    background-color: #3b4253;
    color: #b4b7bd;
  }
  .step-line {
    background-color: #3b4253;
  }
  .step-form-body {
    &::-webkit-scrollbar-track {
      background: #161d31;
    }
    &::-webkit-scrollbar-thumb {
      background: #3b4253;
    }
  }
}
</style>
